import { action, computed, makeObservable, observable, reaction, runInAction, toJS } from "mobx";
import { AppSubStore } from "@/store/types";
import type { AppSubStoreArgs } from "@/store/types";
import type { ICalendarStore } from "@/store/calendar/types";
import { AppStoreCalendarGoogleStore } from "@/store/calendar/google/calendar-google.store";
import { AppStoreCalendarOutlookStore } from "@/store/calendar/outlook/calendar-outlook.store";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { electronModule } from "@/modules/electron";
import { CalendarEvent } from "@/modules/electron/types";
import { api } from "@/modules/api";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { liveQuery, Subscription } from "dexie";
import { DateTime } from "luxon";
import type { ConnectedCalendarEventListResponse } from "@/store/calendar/types";
import { MIN_ATTENDEE_COUNT } from "@/store/calendar/space-account-calendar-event/helpers";

export class AppStoreCalendarStore extends AppSubStore implements ICalendarStore {
  google: AppStoreCalendarGoogleStore;
  outlook: AppStoreCalendarOutlookStore;

  fetchingUpcomingMeetings = false;
  calendarEventsSubscription?: Subscription;
  upcomingEvents: CalendarEvent[] = [];

  constructor(args: AppSubStoreArgs) {
    super(args);

    this.google = new AppStoreCalendarGoogleStore(args);
    this.outlook = new AppStoreCalendarOutlookStore(args);

    makeObservable<
      this,
      "fetchingUpcomingMeetings" | "fetchUpcomingMeetingsFromStore" | "subscribeToCalendarEvents"
    >(this, {
      useInitializeCalendarEffects: false,
      google: observable,
      outlook: observable,
      fetchingUpcomingMeetings: observable,
      calendarEventsSubscription: observable,
      upcomingEvents: observable,
      resetState: action,
      refreshUpcomingMeetings: action,
      isFetchingUpcomingMeetings: computed,
      fetchUpcomingMeetingsFromStore: action,
      subscribeToCalendarEvents: false,
    });
  }

  async resetState() {
    this.google.resetState();
    this.outlook.resetState();
    this.fetchingUpcomingMeetings = false;
    this.calendarEventsSubscription?.unsubscribe();
    this.upcomingEvents = [];
  }

  get isFetchingUpcomingMeetings() {
    return this.fetchingUpcomingMeetings;
  }

  async refreshUpcomingMeetings() {
    if (this.fetchingUpcomingMeetings) return;
    if (!this.google.isConnected && !this.outlook.isConnected) {
      runInAction(() => {
        this.google.upcomingMeetings = [];
        this.outlook.upcomingMeetings = [];
      });
      return;
    }

    runInAction(() => {
      this.fetchingUpcomingMeetings = true;
    });

    try {
      await api.post("/v2/connected-calendars/reconcile", {});
      await this.fetchUpcomingMeetingsFromStore();
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarStore] Failed to reconcile Upcoming meetings",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
    } finally {
      runInAction(() => {
        this.fetchingUpcomingMeetings = false;
      });
    }
  }

  async fetchUpcomingMeetingsFromStore() {
    if (!this.google.isConnected && !this.outlook.isConnected) {
      runInAction(() => {
        this.google.upcomingMeetings = [];
        this.outlook.upcomingMeetings = [];
      });
      return;
    }

    try {
      const response = await api.get("/v2/connected-calendar-events", {
        params: {
          query: {
            limit: 50,
          },
        },
      });

      const data = response.data as ConnectedCalendarEventListResponse | undefined;
      if (!data) {
        throw new Error("Missing connected calendar events payload.");
      }

      // Filter events by provider and update respective stores
      const googleEvents = data.results.filter(event => event.provider_kind === "GOOGLE_CALENDAR");
      const outlookEvents = data.results.filter(
        event => event.provider_kind === "OUTLOOK_CALENDAR"
      );

      if (this.google.isConnected) {
        this.google.setUpcomingMeetingsFromEvents(googleEvents);
      } else {
        runInAction(() => {
          this.google.upcomingMeetings = [];
        });
      }

      if (this.outlook.isConnected) {
        this.outlook.setUpcomingMeetingsFromEvents(outlookEvents);
      } else {
        runInAction(() => {
          this.outlook.upcomingMeetings = [];
        });
      }
    } catch (unknownError) {
      logger.error({
        message: "[AppStoreCalendarStore] Failed to load stored upcoming meetings",
        info: { error: objectModule.safeErrorAsJson(unknownError as Error) },
      });
    }
  }

  private subscribeToCalendarEvents() {
    this.calendarEventsSubscription?.unsubscribe();

    this.calendarEventsSubscription = liveQuery(async () => {
      const now = DateTime.now();
      const upcomingMeetingsStart = now.minus({ minutes: 15 });
      // Include 2 days worth of events and let the desktop app handle what to show.
      const upcomingMeetingsEnd = now.startOf("day").plus({ days: 2 });

      const upcomingEventRecords = await this.store.spaceAccountCalendarEvents.localTable
        .filter(event => {
          if ((event.model_data?.attendee_count ?? 0) < MIN_ATTENDEE_COUNT) return false;

          const startTime = event.model_data?.start_time;
          if (!startTime) return false;

          const eventStart = DateTime.fromISO(startTime);
          return eventStart >= upcomingMeetingsStart && eventStart <= upcomingMeetingsEnd;
        })
        .toArray();

      const events: CalendarEvent[] = [];

      for (const eventRecord of upcomingEventRecords) {
        const eventId = eventRecord.model_id;
        const eventObservable = await this.store.spaceAccountCalendarEvents.getAsync(eventId);
        if (!eventObservable) continue;

        const connection = eventObservable.associatedSpaceAccountCalendarConnection;
        if (!connection) continue;

        const providerKind = connection.providerKind;
        const eventIdValue = eventObservable.associatedConnectedCalendarEventId || eventId;

        if (providerKind === "GOOGLE_CALENDAR") {
          events.push({
            id: eventIdValue,
            calendarEventId: eventId,
            title: eventObservable.primaryLabel ?? undefined,
            url: eventObservable.url ?? undefined,
            platform: "google-meet",
            startTime: eventObservable.startTime ?? undefined,
            endTime: eventObservable.endTime ?? undefined,
            attendees: [],
          });
        } else if (providerKind === "OUTLOOK_CALENDAR") {
          events.push({
            id: eventIdValue,
            calendarEventId: eventId,
            title: eventObservable.primaryLabel ?? undefined,
            url: eventObservable.url ?? undefined,
            platform: "teams",
            startTime: eventObservable.startTime ?? undefined,
            endTime: eventObservable.endTime ?? undefined,
            attendees: [],
          });
        }
      }

      return events;
    }).subscribe({
      next: async (events: CalendarEvent[]) => {
        runInAction(() => {
          this.upcomingEvents = events;
        });
      },
    });
  }

  useInitializeCalendarEffects() {
    /**
     * Subscribe to calendar events and forward changes to the desktop app.
     */
    useEffectOnMount(() => {
      // Subscribe to calendar events from local database
      this.subscribeToCalendarEvents();

      // If it's web rather than desktop there's no problem,
      // we need to observe upcomingMeetings either way.
      const release = reaction(
        () => ({
          upcomingEvents: this.upcomingEvents,
          calendarIntegrationEnabled:
            this.store.spaceAccountFeatureFlags.getFlagStatus("calendar-integration"),
        }),
        ({ upcomingEvents, calendarIntegrationEnabled }) => {
          // Don't send mobx objects to the desktop app.
          const events = !calendarIntegrationEnabled ? [] : toJS(upcomingEvents);
          electronModule.setCalendarEvents({ events });
        }
      );

      return () => {
        release();
        this.calendarEventsSubscription?.unsubscribe();
        electronModule.setCalendarEvents({ events: [] });
      };
    });
  }
}
